import myAxios from './axiositeachWrapper';
import Config from '../_config/config';

export interface QuestionSearchResult {
  id: number;
  name: string;
  stemStatement: string;
  mark: number;
  questionCategory: number | null;
  tags: string | null;
  topicName: string | null;
  difficultyName: string | null;
  questionTypeName: string | null;
  categoryName: string | null;
}

export interface QuestionChoice {
  id?: number;
  name: string;
  choiceString: string;
  correctFlag: number;
  feedback: string | null;
  imageFileName: string | null;
  choiceGroup: number;
}

export interface QuestionTag {
  mappingId: number;
  tagId: number;
  tagName: string;
  tagTypeId: number;
  tagTypeName: string;
}

export interface QuestionDetail {
  Id: number;
  Name: string;
  QuestionName: string;
  QuestionStemStatement: string;
  StemStatement: string;
  Mark: number;
  QuestionCategory: number | null;
  TopicMasterId: number | null;
  DifficultyLevelId: number | null;
  QuestionTypeId: number | null;
  QuestionConceptId: number | null;
  InstructorId: number | null;
  Tags: string | null;
  Explanation: string | null;
  OverallFeedBack: string | null;
  Hint: string | null;
  SolutionLink: string | null;
  Answer: string | null;
  AnswerRangeStart: number | null;
  AnswerRangeEnd: number | null;
  Instructions: string | null;
  CreationDate: string | null;
  topicName: string | null;
  difficultyName: string | null;
  questionTypeName: string | null;
  questionTypeShortCode: string | null;
  categoryName: string | null;
  choices: QuestionChoice[];
  tags: QuestionTag[];
  context: { id: number; name: string; solution: string } | null;
  GraphConfig: any | null;
}

export interface LookupData {
  topics: { id: number; name: string; parentId: number | null; courseId: number | null }[];
  difficulties: { id: number; name: string }[];
  types: { id: number; name: string; shortCode: string }[];
  categories: { id: number; name: string }[];
  tagTypes: { id: number; name: string; tags: { id: number; name: string }[] }[];
  tags: { id: number; name: string; typeId: number; parentId: number | null }[];
}

export interface SearchParams {
  q?: string;
  topicId?: number;
  difficultyId?: number;
  typeId?: number;
  categoryId?: number;
  tagIds?: number[];
  limit?: number;
  offset?: number;
}

export interface QuestionFormData {
  name?: string;
  stemStatement: string;
  mark?: number;
  questionCategory?: number | null;
  topicMasterId?: number | null;
  difficultyLevelId?: number | null;
  questionTypeId?: number | null;
  explanation?: string | null;
  overallFeedback?: string | null;
  hint?: string | null;
  solutionLink?: string | null;
  answer?: string | null;
  instructions?: string | null;
  answerRangeStart?: number | null;
  answerRangeEnd?: number | null;
  contextHtml?: string | null;
  choices?: { name: string; choiceString: string; correctFlag: number; feedback?: string; choiceGroup?: number }[];
  tagIds?: number[];
  graphConfig?: any;
}

export interface AiGenerateParams {
  topicId: number;
  count: number;
  questionTypeId: number;
  difficultyLevelId: number;
  questionCategoryId?: number;
  competencyTagId?: number;
  instructions?: string;
  marks?: number;
}

export interface ParsedChoice {
  text: string;
  choiceString: string;
  isCorrect: boolean;
}

export interface ParsedQuestion {
  questionStatement: string;
  type: string;
  categoryId: number;
  choices: ParsedChoice[];
  textAnswer?: string;
  answerRangeStart?: number;
  answerRangeEnd?: number;
  matchPairs: { left: string; right: string }[];
  blanks: { answer: string }[];
  solution: string;
  positiveMarks: number;
  negativeMarks: number;
  children?: ParsedQuestion[];
  errors: string[];
}

export interface ImportQuestionInput {
  questionStatement: string;
  categoryId: number;
  choices: { text: string; choiceString: string; isCorrect: boolean }[];
  textAnswer?: string;
  answerRangeStart?: number;
  answerRangeEnd?: number;
  matchPairs?: { left: string; right: string }[];
  blanks?: { answer: string }[];
  solution: string;
  positiveMarks: number;
  negativeMarks: number;
  tagIds: number[];
  children?: ImportQuestionInput[];
}

class QuestionBankService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${Config.nodeSiteUrl}v1/question-bank`;
  }

  async search(params: SearchParams): Promise<{ success: boolean; data?: { questions: QuestionSearchResult[]; total: number; limit: number; offset: number }; error?: string }> {
    try {
      const queryParams: any = {};
      if (params.q) queryParams.q = params.q;
      if (params.topicId) queryParams.topicId = params.topicId;
      if (params.difficultyId) queryParams.difficultyId = params.difficultyId;
      if (params.typeId) queryParams.typeId = params.typeId;
      if (params.categoryId) queryParams.categoryId = params.categoryId;
      if (params.tagIds && params.tagIds.length > 0) queryParams.tagIds = params.tagIds.join(',');
      if (params.limit) queryParams.limit = params.limit;
      if (params.offset !== undefined) queryParams.offset = params.offset;

      const response = await myAxios.get(`${this.baseUrl}/search`, { params: queryParams });
      return response.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || !error.response) {
        return { success: false, error: 'Network error — please check your internet connection and try again.' };
      }
      if (error.response?.status === 401 || error.response?.status === 403) {
        return { success: false, error: 'Session expired — please log out and log back in.' };
      }
      return { success: false, error: error.response?.data?.message || 'Failed to search questions' };
    }
  }

  async getById(id: number): Promise<{ success: boolean; data?: QuestionDetail; error?: string }> {
    try {
      const response = await myAxios.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to get question' };
    }
  }

  async create(data: QuestionFormData): Promise<{ success: boolean; data?: { id: number }; error?: string }> {
    try {
      const response = await myAxios.post(`${this.baseUrl}`, data);
      return response.data as any;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to create question' };
    }
  }

  async update(id: number, data: QuestionFormData): Promise<{ success: boolean; data?: { id: number }; error?: string }> {
    try {
      const response = await myAxios.put(`${this.baseUrl}/${id}`, data);
      return response.data as any;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to update question' };
    }
  }

  async createContextQuestions(contextHtml: string, questions: QuestionFormData[]): Promise<{ success: boolean; data?: { contextId: number; questionIds: number[] }; error?: string }> {
    try {
      const response = await myAxios.post(`${this.baseUrl}/context-questions`, { contextHtml, questions });
      return response.data as any;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to create context questions' };
    }
  }

  async remove(id: number): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await myAxios.delete(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to delete question' };
    }
  }

  async getLookups(): Promise<{ success: boolean; data?: LookupData; error?: string }> {
    try {
      const response = await myAxios.get(`${this.baseUrl}/lookups`);
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch lookups' };
    }
  }

  async aiGenerate(params: AiGenerateParams): Promise<{
    success: boolean;
    data?: { questions: QuestionFormData[]; promptSummary: string };
    error?: string;
    quotaExceeded?: boolean;
  }> {
    try {
      const response = await myAxios.post(`${this.baseUrl}/ai-generate`, params);
      return response.data as any;
    } catch (error: any) {
      if (error.response?.status === 429) {
        return {
          success: false,
          error: error.response.data?.error?.message || 'AI usage limit exceeded. Please try again later.',
          quotaExceeded: true,
        };
      }
      return { success: false, error: error.response?.data?.message || 'Failed to generate questions' };
    }
  }

  async parseDocx(file: File): Promise<{ success: boolean; questions?: ParsedQuestion[]; transactionId?: number | null; docxS3Url?: string | null; error?: string }> {
    try {
      const formData = new FormData();
      formData.append('docxFile', file);
      const response = await myAxios.post(`${this.baseUrl}/import-docx/parse`, formData);
      return response.data as any;
    } catch (error: any) {
      console.error('[parseDocx] Full error:', error);
      console.error('[parseDocx] Response:', error.response?.status, error.response?.data);
      console.error('[parseDocx] Code:', error.code, 'Message:', error.message);
      const serverMsg = error.response?.data?.message;
      const detail = serverMsg || error.code || error.message || 'Unknown error';
      return { success: false, error: `Failed to parse DOCX: ${detail}` };
    }
  }

  async getAnalytics(): Promise<{
    success: boolean;
    data?: {
      total: number;
      byType: { id: number; name: string; count: number }[];
      byDifficulty: { id: number; name: string; count: number }[];
      byCategory: { id: number; name: string; count: number }[];
      byTag: { id: number; name: string; typeName: string; count: number }[];
    };
    error?: string;
  }> {
    try {
      const response = await myAxios.get(`${this.baseUrl}/analytics`);
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch analytics' };
    }
  }

  async bulkImportQuestions(questions: ImportQuestionInput[], transactionId?: number | null): Promise<{ success: boolean; questionIds?: number[]; error?: string }> {
    try {
      const response = await myAxios.post(`${this.baseUrl}/import-docx/save`, { questions, transactionId });
      return response.data as any;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to import questions' };
    }
  }

  async convertDocx(
    file: File,
    opts: { useLLM: boolean }
  ): Promise<{
    success: boolean;
    aiEnabled?: boolean;
    aiUsed?: boolean;
    aiReason?: string;
    questions?: ConvertedQuestionSummary[];
    warnings?: ConvertWarning[];
    imageConversion?: { converted: number; failed: number; skipped: number };
    legacyEquationCount?: number;
    ambiguousBlockCount?: number;
    convertedDocxUrl?: string | null;
    sourceDocxUrl?: string | null;
    convertedDocxBase64?: string;
    transactionId?: number | null;
    durationMs?: number;
    error?: string;
  }> {
    try {
      const formData = new FormData();
      formData.append('docxFile', file);
      formData.append('useLLM', opts.useLLM ? 'true' : 'false');
      const response = await myAxios.post(
        `${this.baseUrl}/convert-docx/preview`,
        formData,
      );
      return response.data as any;
    } catch (error: any) {
      const detail =
        error.response?.data?.message ||
        error.code ||
        error.message ||
        'Unknown error';
      return { success: false, error: `Failed to convert DOCX: ${detail}` };
    }
  }

  /* ---- Rubrics (legacy question_rubric table) ---- */
  async listRubrics(id: number): Promise<{ success: boolean; data?: QuestionRubric[]; error?: string }> {
    try {
      const response = await myAxios.get(`${this.baseUrl}/${id}/rubrics`);
      return response.data as any;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to load rubrics' };
    }
  }

  async addRubric(id: number, data: { name?: string; evaluationCriteria: string; score: number }): Promise<{ success: boolean; data?: { id: number }; error?: string }> {
    try {
      const response = await myAxios.post(`${this.baseUrl}/${id}/rubrics`, data);
      return response.data as any;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to add rubric' };
    }
  }

  async updateRubric(rubricId: number, data: { name?: string; evaluationCriteria: string; score: number }): Promise<{ success: boolean; data?: { id: number }; error?: string }> {
    try {
      const response = await myAxios.put(`${this.baseUrl}/rubrics/${rubricId}`, data);
      return response.data as any;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to update rubric' };
    }
  }

  async deleteRubric(rubricId: number): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await myAxios.delete(`${this.baseUrl}/rubrics/${rubricId}`);
      return response.data as any;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to delete rubric' };
    }
  }

  /* ---- Reviews (legacy question.History JSON) ---- */
  async listReviews(id: number): Promise<{ success: boolean; data?: QuestionReview[]; error?: string }> {
    try {
      const response = await myAxios.get(`${this.baseUrl}/${id}/reviews`);
      return response.data as any;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to load reviews' };
    }
  }

  async addReview(id: number, comments: string): Promise<{ success: boolean; data?: QuestionReview[]; error?: string }> {
    try {
      const response = await myAxios.post(`${this.baseUrl}/${id}/reviews`, { comments });
      return response.data as any;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to add review' };
    }
  }

  /* ---- Solution video (S3 + question.SolutionLink) ---- */
  async uploadSolutionVideo(id: number, file: File): Promise<{ success: boolean; data?: { url: string; solutionLink: string }; error?: string }> {
    try {
      const formData = new FormData();
      formData.append('video', file);
      const response = await myAxios.post(`${this.baseUrl}/${id}/solution-video`, formData);
      return response.data as any;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to upload solution video' };
    }
  }

  /* ---- Question doc (S3 + transaction_master, like import) ---- */
  async uploadQuestionDoc(id: number, file: File): Promise<{ success: boolean; data?: { transactionId: number; fileName: string; replaced?: boolean; parsedCount?: number }; warning?: string; error?: string }> {
    try {
      const formData = new FormData();
      formData.append('docxFile', file);
      const response = await myAxios.post(`${this.baseUrl}/${id}/document`, formData);
      return response.data as any;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to upload question doc' };
    }
  }

  async getQuestionDoc(id: number): Promise<{ success: boolean; data?: { fileName: string; url: string } | null; error?: string }> {
    try {
      const response = await myAxios.get(`${this.baseUrl}/${id}/document`);
      return response.data as any;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to load question doc' };
    }
  }

  /** Add tags to a question (additive — keeps existing tags, skips duplicates). */
  async addTags(id: number, tagIds: number[]): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await myAxios.post(`${this.baseUrl}/${id}/tags`, { tagIds });
      return response.data as any;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to add tags' };
    }
  }

  /** Remove a single tag mapping from a question. */
  async removeTag(id: number, mappingId: number): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await myAxios.delete(`${this.baseUrl}/${id}/tags/${mappingId}`);
      return response.data as any;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to remove tag' };
    }
  }
}

export interface QuestionRubric {
  id: number;
  name?: string | null;
  evaluationCriteria: string;
  score: number;
}

export interface QuestionReview {
  comments: string;
  commentedById: number | null;
  commentedByName: string;
  commentedDate: string;
}

export interface ConvertedQuestionSummary {
  index: number;
  number?: number;
  type: string;
  stemPreview: string;
  optionCount: number;
  hasCorrect: boolean;
  hasAnswer: boolean;
  /** Rich HTML stem with MathML + inline image data URLs (safe — emitted by
   *  our backend parser, not user-supplied HTML). Undefined if rich-preview
   *  parsing failed; UI should fall back to stemPreview. */
  stemHtml?: string;
  choicesPreview?: { choiceString: string; isCorrect: boolean; textHtml: string }[];
  answerHtml?: string;
  /** Only set on comprehension parents. Each entry is one sub-question with
   *  its own stem, choices, and answer. The parent's stemHtml is the shared
   *  passage. `number` is the child's source question number (derived
   *  sequentially from the parent's first-child number). */
  childrenPreview?: {
    number?: number;
    type: string;
    stemHtml?: string;
    choicesPreview?: { choiceString: string; isCorrect: boolean; textHtml: string }[];
    answerHtml?: string;
  }[];
}

export interface ConvertWarning {
  questionIndex?: number;
  severity: 'info' | 'warn' | 'error';
  message: string;
}

export default new QuestionBankService();
